/**
 * Joker interpretation (GAME_RULES.md §2).
 *
 * A player's showdown hand is: own 2 hole cards + 4 real board cards
 * (2 flop reals + turn + river) + 1 joker. The joker may be interpreted
 * as any of the 52 - 2 - 4 = 46 cards that are NOT in the player's own
 * exclusion set (own hole cards + visible real board cards). The player
 * picks whichever of the 46 interpretations yields the strongest 5-card
 * hand. Interpretations are independent per player (an opponent's hole
 * cards are not excluded, since they are not visible).
 */

import { DECK_SIZE, makeCard, suitOf, type Card } from "./cards.js";
import { evaluate7, evaluate7WithBestHand } from "./evaluator.js";

export type Hole = readonly [Card, Card];
export type BoardReal = readonly [Card, Card, Card, Card];

export interface JokerInterpretation {
  /** Same comparable integer `evaluateJoker`/`evaluateJokerNaive` return. */
  readonly value: number;
  readonly category: number;
  /** The concrete card (0..51) the joker was interpreted as for this best hand. */
  readonly jokerCard: Card;
  /** The best concrete 5-card hand (may or may not include jokerCard, if the natural cards already made a hand at least as strong). */
  readonly bestFive: readonly Card[];
}

const sevenScratch: Card[] = [0, 0, 0, 0, 0, 0, 0];

function buildExcluded(hole: Hole, board: BoardReal): boolean[] {
  const excluded = new Array<boolean>(DECK_SIZE).fill(false);
  excluded[hole[0]] = true;
  excluded[hole[1]] = true;
  excluded[board[0]] = true;
  excluded[board[1]] = true;
  excluded[board[2]] = true;
  excluded[board[3]] = true;
  return excluded;
}

/**
 * Reference implementation: tries all (up to 46) valid joker
 * interpretations exhaustively and returns the maximum evaluate7 value.
 * Used to validate the pruned `evaluateJoker` via property testing.
 */
export function evaluateJokerNaive(hole: Hole, board: BoardReal): number {
  const excluded = buildExcluded(hole, board);

  sevenScratch[0] = hole[0];
  sevenScratch[1] = hole[1];
  sevenScratch[2] = board[0];
  sevenScratch[3] = board[1];
  sevenScratch[4] = board[2];
  sevenScratch[5] = board[3];

  let best = -1;
  for (let candidate = 0; candidate < DECK_SIZE; candidate++) {
    if (excluded[candidate]) continue;
    sevenScratch[6] = candidate;
    const value = evaluate7(sevenScratch);
    if (value > best) best = value;
  }
  return best;
}

/**
 * Pruned joker evaluation. Equivalent to `evaluateJokerNaive` but tries
 * far fewer than 46 candidates.
 *
 * Soundness argument: for a fixed candidate rank r, the composition of
 * the resulting 7-card hand's rank multiset is identical no matter which
 * available suit is chosen for that rank (only the suit label of one
 * card changes). Therefore every hand category that depends only on rank
 * counts (pair/trips/quads/full house/straight-without-flush/high card)
 * evaluates identically regardless of which available suit is picked for
 * rank r. The ONLY way the suit choice can change the result is if it
 * affects a flush (or straight flush): including the joker card in a
 * flush combo requires at least 4 OTHER cards of that same suit among
 * the 6 fixed cards (hole + board). If there are exactly 4 fixed cards
 * of a suit, choosing the joker as that suit is the only way to
 * *complete* a new flush. If there are already 5+ fixed cards of a
 * suit, a flush exists regardless of the joker's suit — but choosing
 * the joker as that suit can still *improve* the flush by displacing
 * the weakest fixed same-suit card with a higher joker card. Either
 * way, suits with fixed count >= 4 are "critical" and must be tried
 * explicitly; there is at most one such suit (two suits both at >=4
 * would need >=8 of the 6 fixed cards). Any other (non-critical)
 * available suit at rank r gives an identical result to any other
 * non-critical suit at rank r (their fixed count is always < 4, so they
 * can never complete or improve a flush), so only one representative
 * needs to be tried.
 *
 * Net effect: per rank, we try at most 2 candidates (one non-critical
 * representative + the critical suit, if available and different),
 * instead of up to 4. Across all 13 ranks that's at most 26 evaluate7
 * calls, and typically far fewer since many ranks have only 1-2
 * available suits left after exclusions.
 */
export function evaluateJoker(hole: Hole, board: BoardReal): number {
  const excluded = buildExcluded(hole, board);

  // Count how many of the 6 fixed (non-joker) cards belong to each suit.
  const fixedSuitCount = [0, 0, 0, 0];
  fixedSuitCount[suitOf(hole[0])]!++;
  fixedSuitCount[suitOf(hole[1])]!++;
  fixedSuitCount[suitOf(board[0])]!++;
  fixedSuitCount[suitOf(board[1])]!++;
  fixedSuitCount[suitOf(board[2])]!++;
  fixedSuitCount[suitOf(board[3])]!++;

  // A suit is "critical" if the joker's suit can affect flush outcome:
  // either completing a new flush (exactly 4 fixed cards of that suit) or
  // *improving* an already-guaranteed flush by replacing its weakest
  // card with a higher joker card of the same suit (5+ fixed cards of
  // that suit). At most one suit can reach 4+ of the 6 fixed cards
  // (4+4 = 8 > 6), so there is at most one critical suit.
  let criticalSuit = -1;
  for (let s = 0; s < 4; s++) {
    if (fixedSuitCount[s]! >= 4) {
      criticalSuit = s;
      break;
    }
  }

  sevenScratch[0] = hole[0];
  sevenScratch[1] = hole[1];
  sevenScratch[2] = board[0];
  sevenScratch[3] = board[1];
  sevenScratch[4] = board[2];
  sevenScratch[5] = board[3];

  let best = -1;

  for (let r = 0; r < 13; r++) {
    let nonCriticalRep = -1;
    let criticalAvailable = false;

    for (let s = 0; s < 4; s++) {
      const card = makeCard(r, s);
      if (excluded[card]) continue;
      if (s === criticalSuit) {
        criticalAvailable = true;
      } else if (nonCriticalRep === -1) {
        nonCriticalRep = card;
      }
    }

    if (nonCriticalRep !== -1) {
      sevenScratch[6] = nonCriticalRep;
      const value = evaluate7(sevenScratch);
      if (value > best) best = value;
    }
    if (criticalAvailable) {
      sevenScratch[6] = makeCard(r, criticalSuit);
      const value = evaluate7(sevenScratch);
      if (value > best) best = value;
    }
  }

  return best;
}

/**
 * "Explain the result" counterpart to `evaluateJoker`/`evaluateJokerNaive`:
 * returns not just the comparable value but which concrete card the
 * joker was interpreted as, the resulting hand category, and the actual
 * best 5 cards. Used to show players "what the joker turned into" at
 * showdown (see docs/UI_REFERENCE.md). Not on the throughput-critical
 * path (bench targets the value-only `evaluateJoker`), so this simply
 * runs the full 46-candidate exhaustive search using
 * `evaluate7WithBestHand`, which guarantees it agrees with the reference
 * value-only implementation by construction.
 */
export function evaluateJokerDetailed(hole: Hole, board: BoardReal): JokerInterpretation {
  const excluded = buildExcluded(hole, board);

  let best: JokerInterpretation | null = null;

  for (let candidate = 0; candidate < DECK_SIZE; candidate++) {
    if (excluded[candidate]) continue;
    const seven: Card[] = [hole[0], hole[1], board[0], board[1], board[2], board[3], candidate];
    const detail = evaluate7WithBestHand(seven);
    if (best === null || detail.value > best.value) {
      best = {
        value: detail.value,
        category: detail.category,
        jokerCard: candidate,
        bestFive: detail.bestFive,
      };
    }
  }

  if (best === null) {
    // Should be unreachable: at most 6 of 52 cards are excluded, so
    // there are always candidates left.
    throw new Error("evaluateJokerDetailed: no valid joker candidates found.");
  }

  return best;
}
